import Big from 'big.js';

import {
  BUY, DIRECTIONS, TRADE_TYPE_ORDER, TRADE_TYPE_UNSPECIFIED,
} from 'src/util/constants';
import logger from 'src/util/logger';

/**
 * Sentinel key used for the aggregated "Other" series in filtered chart data.
 * Guaranteed not to collide with real trade/meter keys because
 * `buildTradeViewKey` always produces patterns like
 * `parentId_TRADE_TYPE_..._DATA_AGGREGATE_...`, and meter UUIDs are standard UUIDs.
 */
export const OTHER_KEY = '__other__';

/**
 * Prefix used by all typed "Other" sub-series keys emitted by
 * `getFilteredTradeChartDataByVisibleKeys`. The full key shape is
 * `__other_${tradeType}__`, e.g. `__other_TRADE_TYPE_CONTRACTED__` or
 * `__other_untraded__`. The `untraded` suffix intentionally satisfies the
 * `key.includes('untraded')` checks used by `barColor` and the `ChartTrade`
 * zero-filter, so no changes are required in those consumers.
 */
const OTHER_SUB_KEY_PREFIX = '__other_';
const OTHER_SUB_KEY_SUFFIX = '__';

/**
 * Builds the sentinel key for a typed "Other" sub-series.
 * @param {string} tradeType - Trade type constant (e.g. TRADE_TYPE_CONTRACTED,
 *   UNTRADED_ENERGY_KEY).
 * @returns {string} Sub-series key, e.g. `__other_TRADE_TYPE_CONTRACTED__`.
 */
export const buildOtherSubSeriesKey = (tradeType) => (
  `${OTHER_SUB_KEY_PREFIX}${tradeType}${OTHER_SUB_KEY_SUFFIX}`
);

/**
 * Detects whether a chart series key is a typed "Other" sub-series. Excludes the
 * legacy single `OTHER_KEY` sentinel so callers can distinguish the two.
 * @param {string} key - Chart series key.
 * @returns {boolean} True when `key` matches `__other_<tradeType>__`.
 */
export const isOtherSubSeriesKey = (key) => (
  typeof key === 'string'
  && key.startsWith(OTHER_SUB_KEY_PREFIX)
  && key.endsWith(OTHER_SUB_KEY_SUFFIX)
  && key !== OTHER_KEY
);

/**
 * Coerces a value to a finite number, returning 0 for null, undefined, or
 * non-numeric strings that would otherwise cause Big() to throw.
 * @param {*} v - The value to coerce.
 * @returns {number} A finite number.
 */
const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Aggregates all trade series in `chartData` by `tradeType` for each direction,
 * producing a global total that is invariant to pagination and card selection.
 * This total is the invariant used by the top-down "Other" bucket computation:
 * the residual for each type is `globalTotal[type] − visibleSeries[type]`.
 *
 * Series with a null `tradeType` are excluded, mirroring the treatment of
 * null-type hidden series in `getFilteredTradeChartDataByVisibleKeys`.
 *
 * **Contract:** `value`, `volume`, and `carbon` are kept as `Big` instances in the
 * returned structure so that callers can subtract from them without converting back
 * and forth, preserving precision. Callers must convert to `Number` before rendering.
 * @param {object} chartData - Consolidated trade chart data `{ buy: [...], sell: [...] }`.
 * @returns {{ buy: object, sell: object }} Per-direction totals keyed by `tradeType`,
 *   then by timestamp: `{ interval, value: Big, volume: Big, carbon: Big }`.
 */
export const buildGlobalTotalsByType = (chartData) => {
  const result = {};
  DIRECTIONS.forEach((dir) => {
    result[dir] = {};
    const series = chartData[dir];
    if (!series || series.length === 0) return;
    series.forEach((s) => {
      const { tradeType } = s;
      if (!tradeType) return;
      if (!result[dir][tradeType]) result[dir][tradeType] = {};
      const accum = result[dir][tradeType];
      Object.entries(s.data).forEach(([ts, point]) => {
        if (!accum[ts]) {
          accum[ts] = {
            interval: point.interval,
            value: Big(0),
            volume: Big(0),
            carbon: Big(0),
          };
        }
        accum[ts].value = accum[ts].value.plus(safeNum(point.value));
        accum[ts].volume = accum[ts].volume.plus(safeNum(point.volume));
        accum[ts].carbon = accum[ts].carbon.plus(safeNum(point.carbon));
      });
    });
  });
  return result;
};

/**
 * A single data point within a trade series.
 * The fields `value`, `volume`, and `carbon` are summed across hidden series
 * when building the "Other" bucket. `interval` is copied from the first hidden
 * series that writes a given timestamp.
 *
 * **Contract:** adding a new summable field requires updating
 * `getFilteredTradeChartDataByVisibleKeys` and every producer that constructs
 * trade point objects (e.g. `buildTradeViewChartData`). Failing to do so
 * silently drops the field from the "Other" aggregate.
 * @typedef {object} TradePoint
 * @property {{ timestamp: string, length: string }} interval - ISO 8601 interval descriptor.
 * @property {number} value - Energy traded in the active unit.
 * @property {number} volume - Volume traded.
 * @property {number} carbon - Carbon equivalent.
 */

/**
 * A single data point within a meter (historian) direction bucket.
 * The fields `value` and `carbon` are summed across hidden meters when
 * building the "Other" bucket. `timestamp` is copied from the first hidden
 * meter that writes a given timestamp. `flags` are union-merged across all
 * contributing hidden meters so data-quality markers are preserved.
 *
 * **Contract:** adding a new summable field requires updating
 * `getFilteredMeterChartDataByVisibleKeys` and every producer that constructs
 * meter point objects (e.g. `buildMeterViewChartData`). Failing to do so
 * silently drops the field from the "Other" aggregate.
 * @typedef {object} MeterPoint
 * @property {string} timestamp - ISO 8601 timestamp string.
 * @property {number} value - Energy measured in the active unit.
 * @property {Array<{identifier: string, description: string}>} flags - Data-quality flag objects.
 * @property {number|import('big.js').Big} carbon - Carbon equivalent; may arrive as a
 *   Big instance from `buildMeterViewChartData` and is converted to a plain Number on output.
 */

/**
 * Union-merges two flag arrays without duplicates, deduplicating by `identifier`
 * for object flags (e.g. `{ identifier, description }`) or by value for string flags.
 * This is necessary because flag objects from different meters are distinct references
 * even when they share the same identifier, so `new Set()` cannot remove them.
 * @param {Array} existing - Accumulated flags already in the target point.
 * @param {Array} incoming - Flags from the current meter point being merged.
 * @returns {Array} Deduplicated union of both arrays.
 */
const mergeFlags = (existing, incoming) => {
  const seen = new Map(existing.map((f) => [f?.identifier ?? f, f]));
  (incoming ?? []).forEach((f) => {
    const key = f?.identifier ?? f;
    if (!seen.has(key)) seen.set(key, f);
  });
  return [...seen.values()];
};

/**
 * Filters consolidated trade chart data so only the series whose keys are in
 * `visibleKeys` are kept. Typed "Other" sub-series (key `__other_${tradeType}__`) are
 * derived via a top-down subtraction: for each trade type the pre-computed global total
 * is reduced by the values of every visible series that shares that type, leaving the
 * residual as the "Other" value. Residuals that are zero at every timestamp are suppressed.
 * Sub-series are ordered by `TRADE_TYPE_ORDER`. Trade types absent from `TRADE_TYPE_ORDER`
 * are aggregated into a `TRADE_TYPE_UNSPECIFIED` fallback and an error is logged.
 * Series with a null `tradeType` are excluded from both the global total and the residuals.
 *
 * Returns the original `chartData` reference only when `visibleKeys` is falsy or empty.
 * When every series is already visible, a new `{ buy, sell }` object is returned containing
 * the same series (no "Other" buckets are added).
 * @param {object} chartData - Consolidated trade chart data `{ buy: [...], sell: [...] }`.
 * @param {Set<string>} visibleKeys - Set of card keys that are currently visible.
 * @param {(tradeType: string, alsoVisible: boolean) => string} getLabelForOtherSubSeriesFunc -
 *   Callback that returns the translated label for a typed "Other" sub-series.
 *   `alsoVisible` is true when the same `tradeType` also appears in `visibleSeries` or
 *   in `selectedTradeTypes`.
 * @param {Set<string>} [selectedTradeTypes] - Optional set of trade-type constants that the
 *   user has explicitly selected (across all pages). When provided, a hidden sub-series whose
 *   trade type is in this set is always labelled "Other {type}" even if that type has no
 *   visible series on the current page.
 * @param {object|null} [globalTotalsByType] - Pre-computed output of `buildGlobalTotalsByType`.
 *   When provided (e.g. from a memoised caller), the global total is not recomputed, which
 *   makes this call O(visible × timestamps) rather than O(all × timestamps). When omitted,
 *   the total is computed internally from `chartData` as a fallback.
 * @returns {{ buy: Array, sell: Array }} Filtered chart data.
 */
export const getFilteredTradeChartDataByVisibleKeys = (
  chartData,
  visibleKeys,
  getLabelForOtherSubSeriesFunc,
  selectedTradeTypes = new Set(),
  globalTotalsByType = null,
) => {
  if (!visibleKeys || visibleKeys.size === 0) {
    return chartData;
  }

  // Use the pre-computed totals when available; otherwise compute them here (fallback for
  // callers that do not pre-compute, e.g. tests that call the function directly).
  const totals = globalTotalsByType ?? buildGlobalTotalsByType(chartData);

  const result = {};

  DIRECTIONS.forEach((dir) => {
    const series = chartData[dir];
    if (!series || series.length === 0) {
      result[dir] = series || [];
      return;
    }

    const visibleSeries = series.filter((s) => visibleKeys.has(s.key));

    if (visibleSeries.length === series.length) {
      result[dir] = visibleSeries;
      return;
    }

    const visibleTradeTypes = new Set([
      ...visibleSeries.map((s) => s.tradeType).filter(Boolean),
      ...selectedTradeTypes,
    ]);

    const dirTotals = totals[dir] || {};
    const tradeTypeOrderSet = new Set(TRADE_TYPE_ORDER);

    // For each trade type that has a global total, subtract the values of all visible series
    // that share that type. The remaining residual is the "Other" value for that type.
    const residualsByType = {};
    Object.entries(dirTotals).forEach(([type, typeData]) => {
      // Initialise residual from the global total for this type (Big instances).
      const residual = {};
      Object.entries(typeData).forEach(([ts, point]) => {
        residual[ts] = {
          interval: point.interval,
          value: point.value,
          volume: point.volume,
          carbon: point.carbon,
        };
      });

      // Subtract each visible series that shares this trade type.
      visibleSeries.forEach((s) => {
        if (s.tradeType !== type) return;
        Object.entries(s.data).forEach(([ts, point]) => {
          if (!residual[ts]) return;
          residual[ts].value = residual[ts].value.minus(safeNum(point.value));
          residual[ts].volume = residual[ts].volume.minus(safeNum(point.volume));
          residual[ts].carbon = residual[ts].carbon.minus(safeNum(point.carbon));
        });
      });

      // Suppress residuals that are zero at every timestamp: the type is fully on-page.
      const hasNonZero = Object.values(residual).some(
        (point) => !point.value.eq(0) || !point.volume.eq(0) || !point.carbon.eq(0),
      );
      if (hasNonZero) {
        residualsByType[type] = residual;
      }
    });

    const buildSubSeries = (type, residual) => ({
      key: buildOtherSubSeriesKey(type),
      label: getLabelForOtherSubSeriesFunc(type, visibleTradeTypes.has(type)),
      subLabel: null,
      user: null,
      property: null,
      meter: null,
      tradeType: type,
      data: Object.fromEntries(
        Object.entries(residual).map(([ts, point]) => [ts, {
          interval: point.interval,
          value: Number(point.value),
          volume: Number(point.volume),
          carbon: Number(point.carbon),
        }]),
      ),
    });

    // Identify trade types not in TRADE_TYPE_ORDER — these are unexpected.
    // Log an error and merge their residuals into a TRADE_TYPE_UNSPECIFIED fallback so
    // no energy volume is silently lost from the chart totals.
    const unknownTypes = Object.keys(residualsByType).filter(
      (type) => !tradeTypeOrderSet.has(type),
    );

    let unspecifiedResidual = null;
    if (unknownTypes.length > 0) {
      logger.error(
        'getFilteredTradeChartDataByVisibleKeys: unexpected tradeType values not in TRADE_TYPE_ORDER',
        { unknownTypes, direction: dir },
      );

      unspecifiedResidual = {};
      unknownTypes.forEach((type) => {
        Object.entries(residualsByType[type]).forEach(([ts, point]) => {
          if (!unspecifiedResidual[ts]) {
            unspecifiedResidual[ts] = {
              interval: point.interval,
              value: Big(0),
              volume: Big(0),
              carbon: Big(0),
            };
          }
          unspecifiedResidual[ts].value = unspecifiedResidual[ts].value.plus(point.value);
          unspecifiedResidual[ts].volume = unspecifiedResidual[ts].volume.plus(point.volume);
          unspecifiedResidual[ts].carbon = unspecifiedResidual[ts].carbon.plus(point.carbon);
        });
      });
    }

    const typedOtherSeries = TRADE_TYPE_ORDER
      .filter((type) => residualsByType[type])
      .map((type) => buildSubSeries(type, residualsByType[type]));

    if (unspecifiedResidual) {
      typedOtherSeries.push(buildSubSeries(TRADE_TYPE_UNSPECIFIED, unspecifiedResidual));
    }

    result[dir] = [...visibleSeries, ...typedOtherSeries];
  });

  return result;
};

/**
 * Filters meter chart data so only the meter entries whose keys are in
 * `visibleKeys` are kept. All non-visible entries are merged into a single
 * `__other__` entry by summing `value` and `carbon` at each timestamp.
 * Returns the original `chartData` reference only when `visibleKeys` is falsy or
 * empty. When every entry is already visible, a new object is returned
 * containing the same entries (no "Other" entry is added).
 * @param {object} chartData - Meter chart data `{ meterId: { buy, sell, title, identifier } }`.
 * @param {Set<string>} visibleKeys - Set of meter keys currently visible.
 * @param {string} otherLabel - Translated label for the aggregated "Other" entry.
 * @returns {object} Filtered meter chart data.
 */
export const getFilteredMeterChartDataByVisibleKeys = (chartData, visibleKeys, otherLabel) => {
  if (!visibleKeys || visibleKeys.size === 0) {
    return chartData;
  }

  const visibleEntries = {};
  const hiddenKeys = [];

  Object.keys(chartData).forEach((key) => {
    if (visibleKeys.has(key)) {
      visibleEntries[key] = chartData[key];
    } else {
      hiddenKeys.push(key);
    }
  });

  if (hiddenKeys.length === 0) {
    return visibleEntries;
  }

  // Merge all hidden meter entries into a single __other__ entry
  const otherBuy = {};
  const otherSell = {};

  hiddenKeys.forEach((key) => {
    const entry = chartData[key];

    DIRECTIONS.forEach((dir) => {
      const target = dir === BUY ? otherBuy : otherSell;
      const dirData = entry[dir] || {};

      Object.entries(dirData).forEach(([ts, point]) => {
        if (!target[ts]) {
          target[ts] = {
            timestamp: point.timestamp,
            value: Big(0),
            flags: [],
            carbon: Big(0),
          };
        }
        // Union-merge flags so data-quality markers from all hidden meters are preserved.
        target[ts].flags = mergeFlags(target[ts].flags, point.flags);
        // safeNum guards against non-numeric strings that would cause Big() to throw.
        target[ts].value = target[ts].value.plus(safeNum(point.value));
        target[ts].carbon = target[ts].carbon.plus(safeNum(point.carbon));
      });
    });
  });

  const getNumericDirData = (dirData) => Object.fromEntries(
    Object.entries(dirData).map(([ts, point]) => [ts, {
      timestamp: point.timestamp,
      value: Number(point.value),
      flags: point.flags,
      carbon: Number(point.carbon),
    }]),
  );

  return {
    ...visibleEntries,
    [OTHER_KEY]: {
      buy: getNumericDirData(otherBuy),
      sell: getNumericDirData(otherSell),
      title: otherLabel,
      identifier: '',
    },
  };
};
